import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { ArrowLeftRight } from 'lucide-react';

export default function Contra() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { currencySymbol } = useTenantFormat();

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    from_account: '',
    to_account: '',
    amount: '',
    reference_no: '',
    narration: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.bankAccountsList(token).then(r => {
      const items = r.items || [];
      setAccounts(items);
      if (items.length >= 2) {
        setForm(f => ({ ...f, from_account: items[0].id, to_account: items[1].id }));
      } else if (items.length === 1) {
        setForm(f => ({ ...f, from_account: items[0].id }));
      }
    }).catch(() => {});
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setError('Amount must be greater than zero'); return; }
    if (!form.from_account || !form.to_account) { setError('Select both From and To accounts'); return; }
    if (form.from_account === form.to_account) { setError('From and To accounts must be different'); return; }
    setSubmitting(true);
    try {
      const payload = {
        date: form.date,
        from_account: form.from_account,
        to_account: form.to_account,
        amount: amt,
        reference_no: form.reference_no.trim() || null,
        narration: form.narration.trim() || null,
      };
      const r = await api.voucherContraCreate(token, payload);
      toast.show('Contra voucher created', 'success');
      navigate(`/vouchers/${r.entry_id || r.id || ''}`);
    } catch (err) {
      setError(err?.message || 'Failed to create contra voucher');
    } finally { setSubmitting(false); }
  };

  const fromName = accounts.find(a => a.id === form.from_account)?.name || '';
  const toName = accounts.find(a => a.id === form.to_account)?.name || '';

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="voucher-contra-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center">
          <ArrowLeftRight size={20} className="text-blue-600" />
        </div>
        <div>
          <h1 className="text-gray-900 font-extrabold text-xl">Contra Voucher</h1>
          <p className="text-gray-500 text-xs">Transfer between cash and bank accounts</p>
        </div>
      </div>

      <GlassCard className="p-6">
        <form onSubmit={submit} className="space-y-5">
          {error && <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="voucher-error">{error}</div>}

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Date</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} data-testid="v-date"
              className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">From Account</label>
              <select value={form.from_account} onChange={e => set('from_account', e.target.value)} data-testid="v-from-account"
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none">
                <option value="">Select...</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}{a.account_number ? ` (${a.account_number})` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">To Account</label>
              <select value={form.to_account} onChange={e => set('to_account', e.target.value)} data-testid="v-to-account"
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none">
                <option value="">Select...</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}{a.account_number ? ` (${a.account_number})` : ''}</option>)}
              </select>
            </div>
          </div>

          {fromName && toName && fromName !== toName && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-sm">
              <ArrowLeftRight size={14} />
              <span className="font-semibold">{fromName}</span> → <span className="font-semibold">{toName}</span>
            </div>
          )}
          {form.from_account && form.to_account && form.from_account === form.to_account && (
            <div className="px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm font-semibold">
              From and To accounts must be different
            </div>
          )}

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Amount ({currencySymbol})</label>
            <input type="number" step="0.01" min="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00"
              data-testid="v-amount"
              className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm tabular-nums font-semibold focus:border-brand-purple outline-none" />
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Reference No (optional)</label>
            <input type="text" value={form.reference_no} onChange={e => set('reference_no', e.target.value)} placeholder="Cheque no / UTR / Ref"
              data-testid="v-reference"
              className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
          </div>

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Narration</label>
            <textarea value={form.narration} onChange={e => set('narration', e.target.value.slice(0, 200))} maxLength={200} rows={2} placeholder="Description..."
              data-testid="v-narration"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none resize-none" />
            <p className="text-gray-400 text-[10px] text-right mt-0.5">{form.narration.length}/200</p>
          </div>

          <button type="submit" disabled={submitting || (form.from_account === form.to_account)} data-testid="v-submit"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
            {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : 'Create Contra Voucher'}
          </button>
        </form>
      </GlassCard>
    </div>
  );
}
